import styled, { css } from 'styled-components';

import {
  toastEnterDurationMs,
  toastExitDelayMs,
} from '~/components/toasts-layout/toast-timings';
import { ToastPosition } from '~/stores/use-toasts/useToasts';

interface StyledToastProps {
  $position: ToastPosition;
  $offsetY: number;
  $enterFrom?: boolean;
  $exiting?: boolean;
}

const wrapperPaddingX = 20;
export const offsetBetweenToasts = 20;

const absolutePositions = {
  [ToastPosition.TopLeft]: { x: 'left', y: 'top' },
  [ToastPosition.Top]: { x: 'middle', y: 'top' },
  [ToastPosition.TopRight]: { x: 'right', y: 'top' },
  [ToastPosition.BottomLeft]: { x: 'left', y: 'bottom' },
  [ToastPosition.Bottom]: { x: 'middle', y: 'bottom' },
  [ToastPosition.BottomRight]: { x: 'right', y: 'bottom' },
};

const horizontalAnimation = ({
  $position,
  $enterFrom,
  $exiting,
}: StyledToastProps) => css`
  ${($enterFrom || $exiting) &&
  css`
    transform: translateX(
      calc(
        ${absolutePositions[$position].x === 'left' ? '-100% - ' : '100% + '}
          ${wrapperPaddingX}px
      )
    );
  `}

  ${!$enterFrom &&
  !$exiting &&
  css`
    transform: translateX(0px);
  `}
`;

const verticalAnimation = ({
  $offsetY,
  $position,
  $enterFrom,
  $exiting,
}: StyledToastProps) => {
  const calcSign = absolutePositions[$position].y === 'top' ? '-' : '+';

  return css`
    left: 50%;
    transform: translateX(-50%);

    ${($enterFrom || $exiting) &&
    css`
      transform: translate(
        -50%,
        calc(
          ${calcSign}100% ${calcSign} ${$offsetY}px ${calcSign}
            ${offsetBetweenToasts}px
        )
      );
    `}

    ${!$enterFrom &&
    !$exiting &&
    css`
      transform: translate(-50%, 0px);
    `}
  `;
};

export const ToastsWrapper = styled.div`
  position: fixed;
  top: 0;
  pointer-events: ${props => (props.onClick ? 'all' : 'none')};
  width: 100vw;
  height: 100vh;
  z-index: var(--z-index-toast);
  overflow: hidden;
`;

const exitDelayFor = (exiting: boolean | undefined) =>
  exiting ? toastExitDelayMs : 0;

const toastDynamicStyles = (props: StyledToastProps) => {
  const x = absolutePositions[props.$position].x;
  const y = absolutePositions[props.$position].y;
  const exitDelay = exitDelayFor(props.$exiting);
  const leftVal =
    x === 'left' ? `${wrapperPaddingX}px` : x === 'middle' ? '50%' : 'unset';
  const rightVal = x === 'right' ? `${wrapperPaddingX}px` : 'unset';
  const topVal = y === 'top' ? `${props.$offsetY}px` : 'unset';
  const bottomVal = y === 'bottom' ? `${props.$offsetY}px` : 'unset';
  const positionAnimation =
    x === 'middle' ? verticalAnimation(props) : horizontalAnimation(props);
  const transitionValue = `top ${toastEnterDurationMs}ms ${exitDelay}ms cubic-bezier(0.73, 1.45, 0.75, 0.98), bottom ${toastEnterDurationMs}ms ${exitDelay}ms cubic-bezier(0.73, 1.45, 0.75, 0.98), transform ${toastEnterDurationMs}ms cubic-bezier(0.73, 1.45, 0.75, 0.98)`;
  return css`
    transition: ${transitionValue};
    left: ${leftVal};
    right: ${rightVal};
    top: ${topVal};
    bottom: ${bottomVal};
    ${positionAnimation}
  `;
};

export const Toast = styled.div<StyledToastProps>`
  position: absolute;
  pointer-events: all;
  cursor: pointer;
  margin: ${offsetBetweenToasts}px 0;
  display: inline-block;
  ${toastDynamicStyles}
`;
